import { downloadACPdocument, getLogs, getStepOneDetails } from '@api/resident';
import helloLogo from '@assets/logos/hello-my-name-is-logo-web.png';
import UploadImage from '@components/upload-image';
import useWindowWidth from '@hooks/useWindowWidth';
import { ApiResponse, ListApiResponse } from '@typings/ApiResponse';
import { LastedUpdateDateData, ResidentDetails } from '@typings/ResidentTypes';
import React, { useEffect, useState } from 'react';
import './styles.scss';
import SuccessNotificationBanner from '@components/SuccessNotificationBanner';
import ImportantNotificationBanner from '@components/ImportantNotificationBanner';
// eslint-disable-next-line import/no-cycle
import PatientNotesDetailsTable from './PatientNotesDetailsTable';

export interface TableData {
  patientFullName: string;
  careStatus: string;
  acpStatus: string;
  acpReviewDate: string;
  pigStatus: string;
  address: string;
  nfkFullName: string;
  nfkPhoneNumber: string;
}

interface ResidentProfileHeaderProps {
  slug: string | undefined;
  setFullName: (name: string) => void;
  setPrognosisFromData: (prognosis: string) => void;
  isUpdatedPronosis: boolean;
  scrollToPrognosis: () => void;
}

interface ResidentInfor {
  id?: string;
  fullName?: string;
  address?: string;
  city?: string;
  state?: string;
  avatar?: string;
  preferredName?: string;
  careStatus?: string;
}

const ResidentProfileHeader: React.FC<ResidentProfileHeaderProps> = ({
  slug,
  setFullName,
  setPrognosisFromData,
  isUpdatedPronosis,
  scrollToPrognosis,
}) => {
  const [residentInfor, setResidentInfor] = useState<ResidentInfor>({});
  const [tableData, setTableData] = useState({
    patientFullName: '',
    careStatus: '',
    acpStatus: '',
    acpReviewDate: '',
    pigStatus: '',
    address: '',
    nfkFullName: '',
    nfkPhoneNumber: '',
  });
  const width = useWindowWidth();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  useEffect(() => {
    const handleGetResidentDetails = async () => {
      if (slug) {
        try {
          const response: ApiResponse<ResidentDetails> =
            await getStepOneDetails(slug);

          if (response && response.status === 'success') {
            const {
              profile: {
                full_name: fullName,
                nfk_name: nfkFullName,
                nfk_phone: nfkPhoneNumber,
                current_address_line1: currentAddressLine1,
                acp,
                prognosis,
              },
              avatar,
              reviewDate,
              care_status: careStatusData,
              preferredName,
              id,
            } = response.data;

            setResidentInfor({
              fullName,
              avatar,
              preferredName,
              id,
            });
            setFullName(fullName);
            setPrognosisFromData(prognosis);
            setTableData({
              patientFullName: fullName,
              careStatus: careStatusData,
              acpStatus: acp,
              acpReviewDate: reviewDate ?? '',
              pigStatus: prognosis,
              address: currentAddressLine1,
              nfkFullName,
              nfkPhoneNumber,
            });
          }
        } catch (error) {
          console.error(error);
        }
      }
    };
    handleGetResidentDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, setFullName, isUpdatedPronosis]);

  useEffect(() => {
    const handleGetLogs = async () => {
      if (residentInfor.id) {
        try {
          const res: ApiResponse<ListApiResponse<LastedUpdateDateData>> =
            await getLogs(residentInfor.id, 1, 5);
          if (res && res.status === 'success') {
            const updatedAt = res.data?.data?.[0]?.updatedAt;
            if (updatedAt) {
              const formatted = new Date(updatedAt).toISOString().split('T')[0];
              setLastUpdatedAt(formatted);
            }
          }
        } catch (error) {
          console.error(error);
        }
      }
    };
    handleGetLogs();
  }, [residentInfor.id]);

  const handleDownloadPDF = async () => {
    try {
      if (!slug || !residentInfor.id) return;

      setIsDownloading(true);
      setDownloadSuccess(false);

      const response = await downloadACPdocument(residentInfor.id, slug);

      if (!response) {
        throw new Error('No data received from server');
      }

      // Convert response into a blob
      const url = window.URL.createObjectURL(new Blob([response]));

      // Create a temporary download link
      const link = document.createElement('a');
      link.href = url;
      link.download = lastUpdatedAt
        ? `${residentInfor.fullName} - AdvanceCarePlan - ${lastUpdatedAt}.pdf`
        : `${residentInfor.fullName} - AdvanceCarePlan.pdf`; // filename shown to user
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      setErrorMessage(null);
      setDownloadSuccess(true); // ✅ show success message
    } catch (error: any) {
      console.error('Download failed:', error);
      setErrorMessage(
        error?.message ||
          'Something went wrong while downloading the PDF. Please try again.'
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="user-profile-header">
      {width < 641 && (
        <div className="flex justify-start items-center">
          <img src={helloLogo} width={300} alt={helloLogo} />
          <h1 className="govuk-heading-l">
            {residentInfor.preferredName
              ? residentInfor.preferredName
              : residentInfor.fullName}
          </h1>
        </div>
      )}
      {/* ✅ Status messages */}
      {isDownloading && (
        <ImportantNotificationBanner
          title=" Downloading your PDF, please wait..."
          duration={10000}
          noScrollToTop
        />
      )}
      {downloadSuccess && !isDownloading && (
        <SuccessNotificationBanner
          title="Your PDF has been downloaded successfully."
          duration={10000}
          noScrollToTop
        />
      )}
      <div className="govuk-grid-row n:flex n:flex-row-reverse">
        <div className="govuk-grid-column-one-third flex flex-col n:items-center gap-5">
          <UploadImage
            isEditable={false}
            isCustomized
            title="Add a recent profile photo"
            imageUrl={residentInfor?.avatar || ''}
            setImageUrl={() => {}}
          />

          <p className="govuk-body govuk-!-font-weight-bold">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a href="#" className="govuk-link" onClick={handleDownloadPDF}>
              Download Advance Care Plan
            </a>
          </p>
        </div>
        <div className="govuk-grid-column-two-thirds">
          {width >= 641 && (
            <div className="flex justify-start items-center">
              <img src={helloLogo} width={300} alt={helloLogo} />
              <h1 className="govuk-heading-l">
                {residentInfor.preferredName
                  ? residentInfor.preferredName
                  : residentInfor.fullName}
              </h1>
            </div>
          )}
          {slug && (
            <PatientNotesDetailsTable
              tableData={tableData}
              slug={slug}
              scrollToPrognosis={scrollToPrognosis}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ResidentProfileHeader;
